import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import { HomeRoute, ProjectRoute, NewProjectRoute, EditProjectRoute } from './components/desktop/routes';
import RoomListPage from './pages/RoomListPage';
import RoomWorkspacePage from './pages/RoomWorkspacePage';
import RoomFormPage from './pages/RoomFormPage';
import ItemDetailPage from './pages/ItemDetailPage';
import ItemFormPage from './pages/ItemFormPage';
import PhotoListPage from './pages/PhotoListPage';
import ChecklistPage from './pages/ChecklistPage';
import DamageReportListPage from './pages/DamageReportListPage';
import DamageReportDetailPage from './pages/DamageReportDetailPage';
import DamageReportFormPage from './pages/DamageReportFormPage';
import AIAnalysisPage from './pages/AIAnalysisPage';
import AIAnalysisResultsPage from './pages/AIAnalysisResultsPage';
import LoginPage from './pages/LoginPage';
import ErrorBoundary from './components/ErrorBoundary';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { isNotificationEnabled, initializeNotifications } from './services/notificationService';
import { syncOfflinePhotos } from './services/photoService';
import { startUploadQueue } from './services/uploadQueue';
import NotificationDisplay from './components/NotificationDisplay';

function AuthenticatedApp() {
  useEffect(() => {
    const setupNotifications = async () => {
      try {
        if (isNotificationEnabled()) {
          await initializeNotifications();
        }
      } catch (error) {
        console.error('Error initializing notifications on app start:', error);
      }
    };

    setupNotifications();
  }, []);

  // Resume the background upload queue (photos persisted in IndexedDB by an
  // earlier session), and drain the legacy localStorage offline queue: once on
  // startup and again whenever connectivity is restored.
  useEffect(() => {
    void startUploadQueue();

    let syncing = false;
    const runSync = async () => {
      if (syncing || !navigator.onLine) return;
      syncing = true;
      try {
        await syncOfflinePhotos();
      } catch (error) {
        console.error('Error syncing offline photos:', error);
      } finally {
        syncing = false;
      }
    };

    runSync();
    window.addEventListener('online', runSync);
    return () => {
      window.removeEventListener('online', runSync);
    };
  }, []);

  return (
    <NotificationProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={<HomeRoute />} />
            <Route path="projects" element={<HomeRoute />} />
            <Route path="projects/new" element={<NewProjectRoute />} />
            <Route path="projects/:projectId" element={<ProjectRoute />} />
            <Route path="projects/:projectId/edit" element={<EditProjectRoute />} />

            <Route path="projects/:projectId/rooms" element={<RoomListPage />} />
            <Route path="projects/:projectId/rooms/new" element={<RoomFormPage />} />
            <Route path="projects/:projectId/rooms/:roomId" element={<RoomWorkspacePage />} />
            <Route path="projects/:projectId/rooms/:roomId/edit" element={<RoomFormPage />} />

            <Route path="projects/:projectId/items" element={<Navigate to="../rooms" replace />} />
            <Route path="projects/:projectId/items/new" element={<ItemFormPage />} />
            <Route path="projects/:projectId/items/:itemId" element={<ItemDetailPage />} />
            <Route path="projects/:projectId/items/:itemId/edit" element={<ItemFormPage />} />

            <Route path="projects/:projectId/photos" element={<PhotoListPage />} />

            <Route path="projects/:projectId/checklists" element={<ChecklistPage />} />
            
            <Route path="projects/:projectId/damage-reports" element={<DamageReportListPage />} />
            <Route path="projects/:projectId/damage-reports/new" element={<DamageReportFormPage />} />
            <Route path="projects/:projectId/damage-reports/:reportId" element={<DamageReportDetailPage />} />
            <Route path="projects/:projectId/damage-reports/:reportId/edit" element={<DamageReportFormPage />} />
            <Route path="projects/:projectId/items/:itemId/damage-reports/new" element={<DamageReportFormPage />} />

            <Route path="ai-analysis" element={<AIAnalysisPage />} />
            <Route path="ai-analysis/results/:analysisId" element={<AIAnalysisResultsPage />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </NotificationProvider>
  );
}

function AppGate() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-accent-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  return <AuthenticatedApp />;
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <AppGate />
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
